import { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

const DESKTOP_MIN_WIDTH = { minWidth: 1280 };
const DESKTOP_HOOK_MIN_WIDTH = { minWidth: 1279 };
const TABLET_WIDTH = { minWidth: 768, maxWidth: 1279 };
const MOBILE_MAX_WIDTH = { maxWidth: 767 };
const NOT_MOBILE_MIN_WIDTH = { minWidth: 768 };
const BIG_SCREEN_QUERY = { query: '(min-width: 1824px)' };
const PORTRAIT_QUERY = { query: '(orientation: portrait)' };
const RETINA_QUERY = { query: '(min-resolution: 2dppx)' };
const EXTRA_LARGE_MIN_WIDTH = { minWidth: 2200 };

/**
 * Desktop or laptop
 */
const Desktop = ({ children }: { children: JSX.Element }): JSX.Element | null => {
  const isDesktop = useMediaQuery(DESKTOP_MIN_WIDTH);
  return isDesktop ? children : null;
};

/**
 * Tablet
 */
const Tablet = ({ children }: { children: JSX.Element }): JSX.Element | null => {
  const isTablet = useMediaQuery(TABLET_WIDTH);
  return isTablet ? children : null;
};

/**
 * Mobile
 */
const Mobile = ({ children }: { children: JSX.Element }): JSX.Element | null => {
  const isMobile = useMediaQuery(MOBILE_MAX_WIDTH);
  return isMobile ? children : null;
};

/**
 * Not mobile (desktop or laptop or tablet)
 */
const Default = ({ children }: { children: JSX.Element }): JSX.Element | null => {
  const isNotMobile = useMediaQuery(NOT_MOBILE_MIN_WIDTH);
  return isNotMobile ? children : null;
};

export { Default, Desktop, Mobile, Tablet };

/**
 * get device
 * @returns
 */
export function useMediaQueryDevice(): {
  isDesktop: boolean;
  isBigScreen: boolean;
  isTablet: boolean;
  isPortrait: boolean;
  isRetina: boolean;
  isMobile: boolean;
  isNotMobile: boolean;
  isExtraLarge: boolean;
  deviceType: string;
} {
  const isDesktop = useMediaQuery(DESKTOP_HOOK_MIN_WIDTH);
  const isBigScreen = useMediaQuery(BIG_SCREEN_QUERY);
  const isTablet = useMediaQuery(TABLET_WIDTH);
  const isPortrait = useMediaQuery(PORTRAIT_QUERY);
  const isRetina = useMediaQuery(RETINA_QUERY);
  const isMobile = useMediaQuery(MOBILE_MAX_WIDTH);
  const isNotMobile = useMediaQuery(NOT_MOBILE_MIN_WIDTH);
  const isExtraLarge = useMediaQuery(EXTRA_LARGE_MIN_WIDTH);

  const deviceType = useMemo(() => {
    if (isMobile) return 'mobile';
    else if (isTablet) return 'tablet';
    else if (isDesktop) return 'desktop';
    return '';
  }, [isDesktop, isMobile, isTablet]);
  return { isDesktop, isBigScreen, isTablet, isPortrait, isRetina, isMobile, isNotMobile, deviceType, isExtraLarge };
}
